import { createContext, useContext, useState, useCallback } from "react";
import type { ReactNode } from "react";

interface TriageFilters {
  failureLayerFilter: string;
  severityFilter: string;
  conversationIdSearch: string;
  /** ISO date string (YYYY-MM-DD) for filtering to a single day (from heatmap) */
  dateFilter: string;
  /** ISO date string (YYYY-MM-DD) for the start of a date range */
  dateFrom: string;
  /** ISO date string (YYYY-MM-DD) for the end of a date range */
  dateTo: string;
}

interface TriageFilterContextValue extends TriageFilters {
  setFailureLayerFilter: (value: string) => void;
  setSeverityFilter: (value: string) => void;
  setConversationIdSearch: (value: string) => void;
  setDateFilter: (value: string) => void;
  setDateFrom: (value: string) => void;
  setDateTo: (value: string) => void;
  /** Set agent + date together (from heatmap click) */
  setHeatmapFilter: (agent: string, date: string) => void;
  clearHeatmapFilter: () => void;
  /** True when a heatmap cell is actively filtering */
  hasHeatmapFilter: boolean;
}

const TriageFilterContext = createContext<TriageFilterContextValue | null>(null);

export function useTriageFilters(): TriageFilterContextValue {
  const ctx = useContext(TriageFilterContext);
  if (!ctx) {
    throw new Error("useTriageFilters must be used within a TriageFilterProvider");
  }
  return ctx;
}

interface TriageFilterProviderProps {
  children: ReactNode;
}

export const TriageFilterProvider = ({ children }: TriageFilterProviderProps) => {
  const [failureLayerFilter, setFailureLayerFilter] = useState("all");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [conversationIdSearch, setConversationIdSearch] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const hasHeatmapFilter = dateFilter !== "" && failureLayerFilter !== "all";

  const setHeatmapFilter = useCallback((agent: string, date: string) => {
    setFailureLayerFilter(agent);
    setDateFilter(date);
    setDateFrom("");
    setDateTo("");
  }, []);

  const clearHeatmapFilter = useCallback(() => {
    setFailureLayerFilter("all");
    setDateFilter("");
  }, []);

  return (
    <TriageFilterContext.Provider
      value={{
        failureLayerFilter,
        severityFilter,
        conversationIdSearch,
        dateFilter,
        dateFrom,
        dateTo,
        setFailureLayerFilter,
        setSeverityFilter,
        setConversationIdSearch,
        setDateFilter,
        setDateFrom,
        setDateTo,
        setHeatmapFilter,
        clearHeatmapFilter,
        hasHeatmapFilter,
      }}
    >
      {children}
    </TriageFilterContext.Provider>
  );
};
